// ----------------------------------------------------------------------------
// Pixeledit — bucket integration for jspaint
//
// The pixel editor itself is now jspaint (https://github.com/1j01/jspaint),
// loaded directly in the pixeledit.ejs view (no iframe).
// This script handles:
//   - Bucket integration (save canvas to bucket, drop images from bucket)
//   - Drag canvas to bucket
//   - Drop bucket images onto canvas to import
// ----------------------------------------------------------------------------

// Expose theme globals so waveform drawing (draw-utils.cjs) can access theme colors
const _storedTheme = localStorage.getItem('currentTheme');
const _currentTheme = _storedTheme ? JSON.parse(_storedTheme) : (window.Theme || { data: {} });
window.themeColors = Object.values(_currentTheme.data || {});
window.currentTheme = _currentTheme;

const initLegacyBucket = require('./legacy-bucket-integration.js');

const _pixeleditBucket = initLegacyBucket({
    appName: 'pixeledit',
    onBucketDrop: (e) => {
        // Check for jspaint canvas drag
        const isCanvas = e.dataTransfer.getData('application/x-pixeledit-canvas');
        if (isCanvas && _pixeleditBucket.bucket) {
            // Get the main canvas from jspaint
            const canvas = window.main_canvas;
            if (canvas) {
                canvas.toBlob(blob => {
                    if (!blob) return;
                    blob.arrayBuffer().then(data => {
                        const name = `pixeledit_${Date.now()}.png`;
                        _pixeleditBucket.bucket.add({
                            source: 'pixeledit',
                            name,
                            imageData: data,
                            imageFormat: 'image/png',
                            imageMime: 'image/png',
                            fileType: 'image'
                        }).catch(() => {});
                    });
                }, 'image/png');
            }
            return;
        }
    },
});

// Make jspaint main canvas draggable to bucket
setTimeout(() => {
    const canvas = window.main_canvas;
    if (canvas) {
        canvas.draggable = true;
        canvas.addEventListener('dragstart', (e) => {
            e.dataTransfer.effectAllowed = 'copy';
            e.dataTransfer.setData('application/x-pixeledit-canvas', '1');
            e.dataTransfer.setData('text/plain', 'pixedit canvas');
        });
    }
}, 3000);

// Override jspaint save to save to bucket instead of downloading to disk
// Wait for jspaint's systemHooks to be available, then override showSaveFileDialog
setTimeout(() => {
    if (!window.systemHooks || !_pixeleditBucket.bucket) return;

    window.systemHooks.showSaveFileDialog = async ({ getBlob, savedCallbackUnreliable, defaultFileName, defaultFileFormatID, formats, dialogTitle }) => {
        try {
            // Show the save-as prompt so the user can enter a filename and choose format
            const { newFileName, newFileFormatID } = await window.save_as_prompt({
                dialogTitle: dialogTitle || 'Save As',
                defaultFileName: defaultFileName || `pixeledit_${Date.now()}.png`,
                defaultFileFormatID: defaultFileFormatID || 'image/png',
                formats: formats || [
                    { formatID: 'image/png', name: 'PNG', extensions: ['png'], nameWithExtensions: 'PNG (*.png)' },
                    { formatID: 'image/jpeg', name: 'JPEG', extensions: ['jpg', 'jpeg'], nameWithExtensions: 'JPEG (*.jpg;*.jpeg)' },
                    { formatID: 'image/gif', name: 'GIF', extensions: ['gif'], nameWithExtensions: 'GIF (*.gif)' },
                    { formatID: 'image/bmp', name: 'BMP', extensions: ['bmp'], nameWithExtensions: 'BMP (*.bmp)' },
                    { formatID: 'image/webp', name: 'WebP', extensions: ['webp'], nameWithExtensions: 'WebP (*.webp)' },
                ],
                promptForName: true,
            });

            const blob = await getBlob(newFileFormatID);
            if (!blob) return;
            const data = await blob.arrayBuffer();
            const finalName = newFileName.match(/\.(png|jpg|jpeg|gif|bmp|webp)$/i) ? newFileName : newFileName + '.png';
            await _pixeleditBucket.bucket.add({
                source: 'pixeledit',
                name: finalName,
                imageData: data,
                imageFormat: newFileFormatID,
                imageMime: newFileFormatID,
                fileType: 'image'
            });
            // Tell jspaint the file was saved
            if (savedCallbackUnreliable) {
                savedCallbackUnreliable({
                    newFileName: finalName,
                    newFileFormatID: newFileFormatID,
                    newFileHandle: null,
                    newBlob: blob
                });
            }
        } catch (err) {
            console.error('[pixeledit] save to bucket failed:', err);
        }
    };
}, 4000);

// Check URL params for image to load (e.g. /pixeledit?url=...&name=...)
setTimeout(() => {
    const params = new URLSearchParams(window.location.search);
    const imgUrl = params.get('url');
    const imgName = params.get('name') || 'imported.png';
    if (imgUrl && typeof window.open_from_file === 'function') {
        fetch(imgUrl)
            .then(r => r.blob())
            .then(blob => {
                const file = new File([blob], imgName, { type: blob.type || 'image/png' });
                window.open_from_file(file);
                // Clean the URL so it doesn't reload on refresh
                window.history.replaceState({}, document.title, window.location.pathname);
            })
            .catch(err => console.error('[pixeledit] Failed to load image from URL:', err));
    }
}, 5000);

// Allow dropping bucket images onto the jspaint canvas to import
setTimeout(() => {
    const canvas = window.main_canvas;
    const canvasArea = document.querySelector('.canvas-area');
    if (canvas && canvasArea && _pixeleditBucket.bucket) {
        canvasArea.addEventListener('dragover', (e) => {
            if (e.dataTransfer.types.includes('application/x-bucket-item-id')) {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'copy';
            }
        });
        canvasArea.addEventListener('drop', async (e) => {
            const itemId = e.dataTransfer.getData('application/x-bucket-item-id');
            if (itemId) {
                e.preventDefault();
                e.stopPropagation();
                try {
                    const dbItems = await _pixeleditBucket.dbGetAll(_pixeleditBucket.bucket.bucketId);
                    const item = dbItems.find(i => i.id === itemId);
                    if (item && item.type === 'image' && item.wav) {
                        const blob = new Blob([item.wav], { type: item.imageMime || 'image/png' });
                        const fileName = item.name || 'imported.png';

                        // Load the image to get its dimensions for the modal
                        const url = URL.createObjectURL(blob);
                        const img = new Image();
                        img.onload = () => {
                            URL.revokeObjectURL(url);
                            _showImageImportModal(blob, fileName, img.width, img.height);
                        };
                        img.onerror = () => {
                            URL.revokeObjectURL(url);
                            // Fallback: just paste
                            const file = new File([blob], fileName, { type: blob.type });
                            if (typeof window.paste_image_from_file === 'function') {
                                window.paste_image_from_file(file);
                            }
                        };
                        img.src = url;
                    }
                } catch (err) {
                }
            }
        }, true);
    }
}, 3000);

// Modal: ask user whether to add to current canvas or create a new one
function _showImageImportModal(blob, fileName, imgWidth, imgHeight) {
    // Remove any existing modal
    const existing = document.getElementById('pixeledit-import-modal');
    if (existing) existing.remove();

    const modal = document.createElement('div');
    modal.id = 'pixeledit-import-modal';
    modal.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.6);z-index:100001;display:flex;align-items:center;justify-content:center;';
    modal.innerHTML = `
        <div style="background:var(--c1,#1e1e1e);border:1px solid var(--c4,#555);border-radius:8px;padding:24px 32px;max-width:420px;text-align:center;font-family:monospace;">
            <div style="font-size:16px;font-weight:bold;color:var(--cf,#ddd);margin-bottom:4px;">Import Image</div>
            <div style="font-size:12px;color:var(--c9,#888);margin-bottom:16px;">${fileName} (${imgWidth}×${imgHeight})</div>
            <div style="display:flex;flex-direction:column;gap:10px;">
                <button id="pixeledit-import-paste" style="background:var(--c3,#333);color:var(--cf,#ddd);border:1px solid var(--c4,#555);border-radius:4px;padding:10px 20px;cursor:pointer;font-family:monospace;font-size:13px;">Place on active layer</button>
                <button id="pixeledit-import-new" style="background:var(--c3,#333);color:var(--cf,#ddd);border:1px solid var(--c4,#555);border-radius:4px;padding:10px 20px;cursor:pointer;font-family:monospace;font-size:13px;">Open as new canvas (${imgWidth}×${imgHeight})</button>
                <button id="pixeledit-import-cancel" style="background:transparent;color:var(--c9,#888);border:1px solid var(--c4,#555);border-radius:4px;padding:8px 20px;cursor:pointer;font-family:monospace;font-size:12px;">Cancel</button>
            </div>
        </div>
    `;
    document.body.appendChild(modal);

    function close() { modal.remove(); }

    modal.querySelector('#pixeledit-import-paste').onclick = () => {
        close();
        const file = new File([blob], fileName, { type: blob.type });
        // Paste to the selected/active layer if the layer system is available
        if (window.PAINT_LAYERS && typeof window.PAINT_LAYERS.pasteToActiveLayer === 'function') {
            window.PAINT_LAYERS.pasteToActiveLayer(file);
        } else if (typeof window.paste_image_from_file === 'function') {
            window.paste_image_from_file(file);
        }
    };

    modal.querySelector('#pixeledit-import-new').onclick = () => {
        close();
        const file = new File([blob], fileName, { type: blob.type });
        if (typeof window.open_from_file === 'function') {
            window.open_from_file(file);
        }
    };

    modal.querySelector('#pixeledit-import-cancel').onclick = close;
    modal.addEventListener('click', (e) => { if (e.target === modal) close(); });
}
